import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X, Globe } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const linksConfig = [
    { to: "/", key: "home", testid: "nav-home" },
    { to: "/breeds", key: "breeds", testid: "nav-breeds" },
    { to: "/quiz", key: "quiz", testid: "nav-quiz" },
    { to: "/knowledge", key: "knowledge", testid: "nav-knowledge" },
    { to: "/gallery", key: "gallery", testid: "nav-gallery" },
    { to: "/contact", key: "contact", testid: "nav-contact" },
];

function quizPillClass(solid, isActive) {
    const base = "inline-flex items-center gap-2 text-[12px] tracking-wide uppercase font-medium rounded-full px-4 py-2 transition-colors";
    let variant;
    if (solid && isActive) variant = "bg-forest text-paper";
    else if (solid) variant = "bg-gold text-ink hover:bg-forest hover:text-paper";
    else if (isActive) variant = "bg-paper text-ink";
    else variant = "bg-gold text-ink hover:bg-paper";
    return `${base} ${variant}`;
}

function LangToggle({ solid, className = "" }) {
    const { lang, toggle } = useLang();
    // Show the CURRENT language first, then the other — makes the state obvious
    // Same button always toggles, so accidental tap is one tap to revert.
    const isKn = lang === "kn";
    return (
        <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label={isKn ? "Switch to English" : "Switch to Kannada"}
            className={`group inline-flex items-center gap-2 rounded-full border transition-all duration-300 font-medium text-[11px] tracking-widest uppercase px-3.5 py-1.5 ${
                solid
                    ? "border-forest/40 bg-gold/15 text-forest hover:bg-forest hover:text-paper hover:border-forest"
                    : "border-white/50 bg-white/10 backdrop-blur-md text-white hover:bg-white hover:text-ink hover:border-white"
            } ${className}`}
        >
            <Globe size={13} strokeWidth={1.75} className="opacity-90" />
            <span className={`${isKn ? "opacity-100 font-semibold" : "opacity-90"}`}>
                {isKn ? "ಕನ್ನಡ" : "EN"}
            </span>
            <span className="opacity-40">/</span>
            <span className={`${isKn ? "opacity-60" : "opacity-90"}`}>
                {isKn ? "EN" : "ಕನ್ನಡ"}
            </span>
        </button>
    );
}

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { t } = useLang();

    const isHome = location.pathname === "/";
    const solid = scrolled || !isHome || open;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const links = linksConfig.map((l) => ({ ...l, label: t(`common.nav.${l.key}`) }));

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ease-editorial ${
                solid
                    ? "bg-paper/90 backdrop-blur-xl border-b border-border text-ink"
                    : "bg-transparent text-white"
            }`}
        >
            <div className="container-editorial flex items-center justify-between py-5 md:py-6 gap-4">
                <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
                    <img
                        src={solid ? "/brand/logo-dark.png" : "/brand/logo-white.png"}
                        alt="Omkar Dairy Farm"
                        className="h-14 md:h-20 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {links.map((l) => {
                        if (l.to === "/quiz") {
                            return (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={l.testid}
                                    className={({ isActive }) => quizPillClass(solid, isActive)}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-forest" />
                                    {l.label}
                                </NavLink>
                            );
                        }
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={l.testid}
                                className={({ isActive }) =>
                                    `text-[13px] tracking-wide uppercase font-medium link-underline transition-opacity ${
                                        isActive ? "opacity-100" : "opacity-75 hover:opacity-100"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        );
                    })}
                    <LangToggle solid={solid} />
                    <button
                        data-testid="nav-search"
                        aria-label="Search"
                        className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                        <Search size={18} strokeWidth={1.5} />
                    </button>
                </nav>

                <div className="lg:hidden flex items-center gap-3">
                    <LangToggle solid={solid} />
                    <button
                        aria-label="Menu"
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-paper text-ink" data-testid="mobile-menu">
                    <div className="container-editorial py-8 flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`m-${l.testid}`}
                                className="font-serif text-3xl tracking-tight"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
